import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SignupLink from '@/components/SignupLink';
import { getSignupHref } from '@/lib/attribution';
import { Button } from '@/components/ui/button';
import { useAuthState } from '@/hooks/use-auth-state';
import Logo from './Logo';
import NavLinks, { NavSection } from './NavLinks';
import UserMenu from './UserMenu';
import MobileMenuButton from './MobileMenuButton';
import MobileMenu from './MobileMenu';
import RegisterDialog from '../landing/RegisterDialog';
import { COMPARISON_PAGE_PATH } from '@/data/nutritionSoftwareComparison';

const LANDING_SECTION_IDS = [
  'about-us',
  'features',
  'why-we-exist',
  'pricing',
  'blogs'
];
const START_SECTION_IDS = ['about-us', 'features', 'how-we-work', 'for-who'];
const COMPANION_SECTION_IDS = ['what-is-it', 'how-you-connect', 'privacy-and-security', 'faq'];
const COMPANION_NAV_SECTIONS: NavSection[] = [
  { href: '#what-is-it', label: 'What It Is' },
  { href: '#how-you-connect', label: 'How It Works' },
  { href: '#privacy-and-security', label: 'Privacy' },
  { href: '#faq', label: 'FAQ' },
];
const START_NAV_SECTIONS: NavSection[] = [
  { href: '#top', label: 'Home' },
  { href: '#about-us', label: 'About Us' },
  { href: '#features', label: 'Features' },
  { href: '#for-who', label: 'Who Is It For' },
];
const PERSONALISED_NUTRITION_NAV_SECTIONS: NavSection[] = [
  { href: '#why-generic-plans-fail', label: 'Overview' },
  { href: '#how-we-support-planning', label: 'Your Workspace' },
  { href: '#key-inputs', label: 'Key Inputs' },
  { href: '#how-it-works', label: 'How It Works' },
];
const PERSONALISED_NUTRITION_SECTION_IDS = PERSONALISED_NUTRITION_NAV_SECTIONS
  .filter(section => section.href.startsWith('#'))
  .map(section => section.href.slice(1));
const COMPARISON_NAV_SECTIONS: NavSection[] = [
  { href: '#comparison', label: 'Comparison' },
  { href: '#who-its-for', label: 'Who It’s For' },
  { href: '#roadmap', label: 'Roadmap' },
  { href: '#get-started', label: 'Get Started' },
  { href: '#faq', label: 'FAQs' },
];
const COMPARISON_SECTION_IDS = COMPARISON_NAV_SECTIONS.map(section => section.href.slice(1));
const HOME_ACTIVE_SCROLL_THRESHOLD = 80;

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('home');
  const { isLoggedIn, isLandingPage } = useAuthState();
  const { pathname, search } = useLocation();
  const isBlogPage = pathname.startsWith('/blog/');
  const isCompanionAppPage = pathname === '/companion-app';
  const isStartPage = pathname === '/start';
  const isPersonalisedNutritionPage = pathname === '/personalised-nutrition-platform';
  const isComparisonPage = pathname === COMPARISON_PAGE_PATH;
  const isMarketingSubPage =
    isBlogPage || isComparisonPage ||
    pathname === '/personalised-nutrition-platform' ||
    pathname === '/platform/biomarker-informed-nutrition';
  const useLandingChrome = isLandingPage || isCompanionAppPage || isMarketingSubPage || isStartPage;
  const useTransparentLandingNav = isCompanionAppPage || isMarketingSubPage;
  const navSections = isCompanionAppPage
    ? COMPANION_NAV_SECTIONS
    : isStartPage
      ? START_NAV_SECTIONS
      : isPersonalisedNutritionPage
        ? PERSONALISED_NUTRITION_NAV_SECTIONS
        : isComparisonPage
          ? COMPARISON_NAV_SECTIONS
          : undefined;
  const showQuickLinks = (isLandingPage || isStartPage || isComparisonPage || isPersonalisedNutritionPage) && !isCompanionAppPage;
  const treatAsLandingNav = isLandingPage || isStartPage;

  useEffect(() => {
    const sectionIds = isLandingPage
      ? LANDING_SECTION_IDS
      : isStartPage
        ? START_SECTION_IDS
        : isCompanionAppPage
          ? COMPANION_SECTION_IDS
          : isPersonalisedNutritionPage
            ? PERSONALISED_NUTRITION_SECTION_IDS
            : isComparisonPage
              ? COMPARISON_SECTION_IDS
              : null;
    if (!sectionIds) return;
    const updateActiveSection = () => {
      const triggerLine = 150;
      let active = isPersonalisedNutritionPage ? PERSONALISED_NUTRITION_SECTION_IDS[0] : 'home';
      let bestTop = -Infinity;

      if (window.scrollY < HOME_ACTIVE_SCROLL_THRESHOLD) {
        setActiveSection(active);
        return;
      }

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= triggerLine && top > bestTop) {
          bestTop = top;
          active = id;
        }
      }
      setActiveSection(active);
    };
    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveSection);
  }, [isLandingPage, isCompanionAppPage, isStartPage, isPersonalisedNutritionPage, isComparisonPage]);

  const handleOpenRegisterDialog = () => {
    setIsRegisterDialogOpen(true);
  };

  return (
    <nav
      id="site-navigation"
      className={`w-full sticky top-0 z-50 ${
        useLandingChrome ? `${useTransparentLandingNav ? 'bg-transparent' : 'bg-white'} pt-3 md:pt-7` : 'bg-white shadow-sm'
      }`}
    >
      {useLandingChrome ? (
        <div className="landing-container">
          <div className="rounded-[1.375rem] border border-[#E7EEF2] bg-white px-4 py-3 sm:px-6 sm:py-3.5 shadow-[0_0.75rem_1.875rem_rgba(15,23,42,0.08)]">
            <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 xl:gap-5 2xl:gap-6">
              <div className="shrink-0">
                <Logo variant="landing" />
              </div>

              <div className="hidden min-w-0 flex-1 justify-center overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:flex [&::-webkit-scrollbar]:hidden">
                <NavLinks
                  isLandingPage={treatAsLandingNav}
                  activeSection={activeSection}
                  onOpenRegisterDialog={handleOpenRegisterDialog}
                  sections={navSections}
                  showQuickLinks={showQuickLinks}
                  density="compact"
                  scrollWithoutHash={isStartPage}
                />
              </div>

              <div className="ml-auto flex shrink-0 items-center gap-2 lg:hidden">
                <MobileMenuButton isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
              </div>

              <div className="hidden shrink-0 sm:block">
                <Button asChild className="h-10 sm:h-11 2xl:h-12 shrink-0 rounded-[0.625rem] bg-[#40A8F4] px-3 sm:px-4 xl:px-5 2xl:px-7 text-white hover:bg-[#329CE8]">
                  <Link
                    to={isStartPage ? getSignupHref(search) : '/login'}
                    data-analytics-event="Landing CTA Clicked"
                    data-analytics-id={isStartPage ? 'start_navbar_start_free' : 'navbar_login_signup'}
                    data-analytics-label={isStartPage ? 'Start Free' : 'Login/Signup'}
                    data-analytics-section="navbar"
                  >
                    <span className="text-sm sm:text-base 2xl:text-[1.125rem] font-semibold leading-[1.4rem] text-white whitespace-nowrap">
                      {isStartPage ? 'Start Free' : 'Login/Signup'}
                    </span>
                  </Link>
                </Button>
              </div>
            </div>

          <MobileMenu
            isMenuOpen={isMenuOpen}
            isLandingPage={isLandingPage}
            isLoggedIn={isLoggedIn}
            setIsMenuOpen={setIsMenuOpen}
            onOpenRegisterDialog={handleOpenRegisterDialog}
            activeSection={activeSection}
            sections={navSections}
            chrome={useLandingChrome}
            showQuickLinks={showQuickLinks}
            showLandingActions={isLandingPage || isMarketingSubPage}
          />
          </div>
        </div>
      ) : (
        <div className="cs-container py-2 bg-white">
          <div className="w-full flex items-center justify-between">
            <Logo variant={isMarketingSubPage ? 'landing' : 'default'} />

            <div className="hidden md:flex items-center ml-6">
              <NavLinks isLandingPage={isLandingPage} onOpenRegisterDialog={handleOpenRegisterDialog} />
            </div>

            <div className="hidden md:flex items-center ml-auto space-x-4">
              {isLoggedIn ? (
                <UserMenu isLoggedIn={isLoggedIn} />
              ) : (
                <div className="flex items-center space-x-2">
                  <Button asChild variant="outline" className="border-cs-primary text-cs-primary hover:bg-cs-primary/10">
                    <Link to="/login">Log In</Link>
                  </Button>
                  <Button asChild className="bg-cs-primary text-white hover:bg-cs-primary/90">
                    <SignupLink>Sign Up</SignupLink>
                  </Button>
                </div>
              )}
            </div>

            <div className="md:hidden flex items-center">
              <MobileMenuButton isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
            </div>
          </div>

          <MobileMenu
            isMenuOpen={isMenuOpen}
            isLandingPage={isLandingPage}
            isLoggedIn={isLoggedIn}
            setIsMenuOpen={setIsMenuOpen}
            onOpenRegisterDialog={handleOpenRegisterDialog}
            activeSection={activeSection}
          />
        </div>
      )}

      <RegisterDialog isOpen={isRegisterDialogOpen} onOpenChange={setIsRegisterDialogOpen} />
    </nav>
  );
};

export default Navbar;
